import { cloneDeep, isEqual, isObject, omit, pick } from 'lodash';
import { v4 as uuid } from 'uuid';
import { unitToPixels } from 'shared/libs/utils/numbers';
import { DefaultQrCode, DefaultTextbox, DefaultImage, DEFAULT_FONT } from 'shared/libs/providers/badge-editor/enum';

const DEFAULT_MODEL = {
    width: 4,
    height: 6,
    dpi: 203,
    pages: [],
    name: '',
    preset: '4x6_double_sided_tear_resistant_zebra_zd621',
    format: '4x6" Paper badges - Double sided (Zebra ZD621)',
    unit: 'in',
};

export const EMPTY_PAGE = {
    blocks: {
        background: {},
        text: [],
        qrcode: [],
        image: [],
    },
};

/** @enum {'single'|'double'|'mirrored'} */
export const MultipageOptions = Object.freeze({
    SINGLE: 'single',
    DOUBLE: 'double',
    MIRRORED: 'mirrored'
});

/** @enum {'panning'|'background'|'textbox'|'qrcode'} */
export const ToolName = Object.freeze({
    BACKGROUND: 'background',
    IMAGE: 'image',
    PANNING: 'panning',
    QR_CODE: 'qrcode',
    TEXT_BOX: 'textbox',
});

export const UNIT_LABELS = Object.freeze({
    IN: 'Inches',
    MM: 'Milimeters',
});

/**
 * Removes fields changed because of save
 */
const withoutRev = (data) => structuredClone(omit(data, ['_rev', 'fp_last_changes']));

/**
 *
 * @type {({ width: number, height: number, scaleX?: number, scaleY?: number }) => { width: number, height: number }}
 */
const calculateScaledDimensions = (object) => {
    return {
        width: Math.round(object.width * (object.scaleX || 1)),
        height: Math.round(object.height * (object.scaleY || 1)),
    };
};

/**
 * Checks if the document has been changed
 * @type {(docToSave: Record<string,unknown>, docInDb: Record<string,unknown>) => boolean}
 */
export const docIsUnChanged = (docToSave, docInDb) => {
    return isEqual(
        withoutRev(docToSave),
        withoutRev(docInDb),
    );
};

/**
 * @param {Record<string,unknown>} object
 * @param {boolean} keepObjectId
 * @param {string[]} allowedFonts
 * @returns {BadgeEditor.Textbox}
 */
export const sanitizeTextObject = (object, keepObjectId, allowedFonts) => {
    // set default values
    /** @type {BadgeEditor.Textbox} */
    const result = {};
    for (const [key, value] of Object.entries(DefaultTextbox)) {
        result[key] = object[key] || value;

        // only keep allowed fonts if defined (in modelToDoc)
        if (key === 'fontFamily' && allowedFonts && !allowedFonts.includes(object.fontFamily)) {
            result[key] = DEFAULT_FONT;
        }
    }

    if (keepObjectId) {
        result._id = object._id;
    }

    // ensure fontSize and minFontSize are rounded
    result.fontSize = Math.round(result.fontSize);
    result.minFontSize = Math.round(result.minFontSize);

    // ensure minFontSize is smaller than fontSize
    if (result.minFontSize > result.fontSize) {
        result.minFontSize = result.fontSize - 1;
    }

    return result;
};

/**
 * @param {Record<string,unknown>} object
 * @param {boolean} keepObjectId
 * @returns {BadgeEditor.QrCode}
 */
export const sanitizeQrCode = (object, keepObjectId) => {
    // set default values
    /** @type {BadgeEditor.QrCode} */
    const result = {};
    for (const [key, value] of Object.entries(DefaultQrCode)) {
        result[key] = object[key] || value;
    }

    if (keepObjectId) {
        result._id = object._id;
    }

    const { width, height } = calculateScaledDimensions(object);
    result.width = width;
    result.height = height;

    result.isQrCode = true;

    return result;
};

/**
 * @param {Record<string,unknown>} object
 * @param {boolean} keepObjectId
 * @returns {BadgeEditor.Image}
 */
export const sanitizeImage = (object, keepObjectId) => {
    /** @type {BadgeEditor.Image} */
    const result = {};

    for (const [key, value] of Object.entries(DefaultImage)) {
        result[key] = object[key] || value;
    }

    if (keepObjectId) {
        result._id = object._id;
    }

    const { width, height } = calculateScaledDimensions(object);
    result.width = width;
    result.height = height;

    result.isImage = true;

    return result;
};

/**
 * @param {{ blocks: Record<string,unknown> }} page
 * @param {boolean} [keepObjectId]
 * @param {string[]} [allowedFonts]
 */
export const sanitizePage = ({ blocks }, keepObjectId, allowedFonts) => ({
    blocks: {
        background: { ...(pick(blocks?.background, 'id')) },
        text: (Array.isArray(blocks?.text) ? blocks.text : [])
            .map(text => sanitizeTextObject(text, keepObjectId, allowedFonts)),
        qrcode: (Array.isArray(blocks?.qrcode) ? blocks.qrcode : [])
            .map(qrCode => sanitizeQrCode(qrCode, keepObjectId)),
        image: (Array.isArray(blocks?.image) ? blocks.image : [])
            .map(image => sanitizeImage(image, keepObjectId)),
    },
});

/** @type {(textboxes: BadgeEditor.Textbox[], fonts: string[]) => void} */
const textboxesToDoc = (textboxes, fonts) => {
    const validFonts = new Set(fonts.filter(id => id !== DEFAULT_FONT));

    for (const textbox of textboxes) {
        if (validFonts.has(textbox.fontFamily)) {
            textbox.fontFaceId = textbox.fontFamily;
        }
        delete textbox.fontFamily;
    }
};

/**
 * Cleans up the model so that it is in the format expected to be saved
 * @param {Record<string,unknown>} model
 * @param {string[]} allowedFonts
 * @param {MultipageOptions} multipageOption
 */
export const modelToDoc = (model, allowedFonts, multipageOption) => {
    const clone = cloneDeep(model);

    // sanitize pages
    clone.pages = clone.pages.length
        ? clone.pages.map(page => sanitizePage(page, false, allowedFonts))
        : [sanitizePage(EMPTY_PAGE, false, allowedFonts)];

    // add size in pixels to background
    for (const page of clone.pages) {
        if (!page.blocks) page.blocks = {};
        if (!page.blocks.background) page.blocks.background = {};
        page.blocks.background.width = unitToPixels(clone.width, clone.dpi, clone.unit);
        page.blocks.background.height = unitToPixels(clone.height, clone.dpi, clone.unit);

        textboxesToDoc(page.blocks.text, allowedFonts);

        // for the sake of backward compatibility
        page.blocks.images = page.blocks.image || [];
        delete page.blocks.image;
    }

    // ensure mirrored and pages are correct depending on multipageOptions
    const firstPage = clone.pages.at(0);
    switch (multipageOption) {
        case MultipageOptions.MIRRORED:
            clone.mirrored = true;
            firstPage.mirrored = true;
            clone.pages = [firstPage];
            break;
        case MultipageOptions.DOUBLE:
            clone.mirrored = false;
            delete firstPage?.mirrored;
            break;
        case MultipageOptions.SINGLE:
            clone.mirrored = false;
            clone.pages = [firstPage];
            delete firstPage?.mirrored;
            break;
    }

    return clone;
};

/** @type {(block: BadgeEditor.ImageBlock|BadgeEditor.TextBlock|BadgeEditor.QrCodeBlock|BadgeEditor.BackgroundBlock) => void} */
const addBlockId = block => { if (block && isObject(block)) { block._id = uuid(); } };

/** @type {(textboxes: BadgeEditor.Textbox[]) => void} */
const textboxesToModels = textboxes => {
    if (Array.isArray(textboxes)) {
        for (const textbox of textboxes) {
            addBlockId(textbox);
            textbox.fontFamily = textbox.fontFaceId || textbox.fontFamily || DEFAULT_FONT;
            delete textbox.fontFaceId;
        }
    }
};

/** @type {(page: Record<string,unknown>) => void} */
const addInternalIdsToPageObjects = (page) => {
    const addIdsToObjectsInArray = array => {
        if (Array.isArray(array)) {
            for (const object of array) {
                addBlockId(object);
            }
        }
    };
    addBlockId(page.blocks.background);
    addIdsToObjectsInArray(page.blocks.qrcode);
    addIdsToObjectsInArray(page.blocks.images);
    textboxesToModels(page.blocks.text);
};

export const docToModel = (doc = DEFAULT_MODEL) => {
    const model = cloneDeep(doc);
    // add internal ids needed for fabric (removed when saved)
    for (const page of model.pages) {
        addInternalIdsToPageObjects(page);

        // for the sake of backward compatibility
        page.blocks.image = page.blocks.images || [];
        delete page.blocks.images;
    }

    model.userUnit = doc.userUnit || doc.unit;

    return model;
};

/**
 * @param {string} url
 * @returns {Promise<Image>}
 */
export const getImageFromUrl = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => resolve(img);
        img.src = url;
    });

/**
 *
 * @param {BadgeEditor.Textbox|BadgeEditor.Image|BadgeEditor.QrCode} value
 * @returns {'text'|'image'|'qrcode'}
 */
export const getObjectType = (value) => {
    if (value.isQrCode) {
        return 'qrcode';
    }
    if (value.isImage) {
        return 'image';
    }
    return 'text';
};
